import type { CustomerTeamMember } from '@clockwise/clients/customer-team';
import { findCoveringMemberships, membershipsContentKey } from '@clockwise/shared';
import { useCallback, useMemo, useRef } from 'react';

/**
 * Module-level singleton so the "no customer yet" branch returns a
 * stable reference across renders. Critical for callers that pass the
 * result inline as a memo dep (e.g. `filterServiceRoleIds={lookup(...)}`).
 */
const EMPTY_ROLE_IDS: readonly string[] = Object.freeze([]);

/**
 * Stable per-(customerId, date, fallbackRoleId) role-id lookup over a
 * memberships array. Returns the SAME array reference across renders
 * for unchanged inputs — so callers can pass the result inline as
 * `filterServiceRoleIds={lookup(...)}` to a memoized child without
 * invalidating downstream memos on every render.
 *
 * Why this hook exists:
 *
 *   1. **Render purity.** A naive `useMemo(() => new Map(), [memberships])`
 *      pattern would mutate the Map inside a useCallback that runs
 *      during render — under StrictMode dev or future concurrent
 *      features the memo MAY be discarded mid-render and the cache
 *      silently empties. Using a useRef-backed cache, mutated via a
 *      synchronous sentinel check at the top of the hook, sidesteps
 *      that.
 *
 *   2. **Content-stable invalidation.** React Query produces a NEW
 *      `memberships` array reference per refetch even when content is
 *      byte-identical. Keying the cache on a content hash (via
 *      `membershipsContentKey`) keeps the cache warm across the 60 s
 *      refetch / focus / reconnect cadence.
 *
 *   3. **biome-ignore scoping.** The useCallback's deps deliberately
 *      EXCLUDE `memberships` (read via `membershipsRef.current` so
 *      identity stays tied to contentKey, not the array reference).
 *      Keeping the suppression inside this hook means future edits to
 *      the day-list / week-grid / timer-popover that use the hook
 *      can't accidentally pick up a stale-closure bug — the
 *      suppression is co-located with the cache, not shared with
 *      business logic.
 *
 * Fallback semantics (when no membership covers `(customerId, date)`):
 *   - existing entry → caller passes the entry's pinned `roleId`
 *   - draft (no entry yet) → caller passes the assignee's global role
 *   - no customer yet → returns the module-level `EMPTY_ROLE_IDS`
 *     (picker reads it as "no filter, show the whole catalog" — but
 *     the picker is also typically disabled when no customer is set,
 *     so this branch is defensive).
 *
 * The callback receives a fresh `(customerId, date, fallbackRoleId)` on
 * every invocation, so the cache hit returns the SAME array reference
 * for identical inputs across renders.
 */
export function useStableRoleLookup(
  memberships: readonly CustomerTeamMember[],
): (customerId: string | null, date: string, fallbackRoleId: string) => readonly string[] {
  const contentKey = useMemo(() => membershipsContentKey(memberships), [memberships]);

  // The cache lives in a single ref-bound object so reset-on-content-
  // change is atomic. Mutating `cacheRef.current` during render is
  // explicitly blessed for prop-derived state (React docs § "Avoiding
  // recreating the ref contents"). Idempotent under StrictMode
  // double-render: the second pass sees `key === contentKey` and the
  // branch no-ops.
  const cacheRef = useRef<{ key: string; map: Map<string, readonly string[]> }>({
    key: '',
    map: new Map(),
  });
  if (cacheRef.current.key !== contentKey) {
    cacheRef.current = { key: contentKey, map: new Map() };
  }

  // `membershipsRef` lets the callback read the latest memberships
  // without depending on the array reference (which churns on each
  // refetch). The ref-update line runs every render BEFORE the
  // useCallback, so by the time the callback is invoked downstream the
  // ref already holds the current memberships.
  const membershipsRef = useRef(memberships);
  membershipsRef.current = memberships;

  return useCallback(
    (customerId: string | null, date: string, fallbackRoleId: string): readonly string[] => {
      if (!customerId) return EMPTY_ROLE_IDS;
      const cacheKey = `${customerId}|${date}|${fallbackRoleId}`;
      const cached = cacheRef.current.map.get(cacheKey);
      if (cached) return cached;
      // Multi-role: union the roles of EVERY covering membership so the
      // picker scopes the catalog to the union of activities the user can
      // log under any of their roles on this customer. The chosen activity
      // narrows back to one role on save (server-derived, or prompted when
      // the activity is loggable under more than one).
      const hits = findCoveringMemberships(membershipsRef.current, customerId, date);
      const result =
        hits.length > 0 ? Array.from(new Set(hits.map((m) => m.serviceRoleId))) : [fallbackRoleId];
      cacheRef.current.map.set(cacheKey, result);
      return result;
    },
    // contentKey is the only reactive dep — memberships is intentionally
    // read via the ref so the callback identity survives same-content
    // refetches. Suppression is local to this hook so future callers
    // can't accidentally inherit the closure-staleness suppression in
    // their own callbacks.
    [contentKey],
  );
}
